import { useCallback, useEffect, useState } from "react"
import api from "../services/api"
import AdminLayout from "../components/AdminLayout"

const C = {
    navy: "#002B54",
    navyMid: "#1e4070",
    blue: "#1a6fc4",
    bluePale: "#e8f0fb",
    bluePale2: "#dceaf8",
    white: "#ffffff",
    offWhite: "#f5f7fa",
    gray100: "#eef1f5",
    gray200: "#dde2ea",
    gray300: "#c3cad5",
    gray400: "#94a0b0",
    gray500: "#64748b",
    gray700: "#334155",
    green: "#15803d",
    greenBg: "#dcfce7",
    red: "#dc2626",
    redBg: "#fef2f2",
    orange: "#F4811F",
    orangeBg: "#fff4eb",
    purple: "#7c3aed",
    purpleBg: "#f5f3ff",
}

function actionStyle(action) {
    if (!action) return { bg: C.gray100, color: C.gray500 }
    if (action.includes("rejected") || action.includes("revoked") || action.includes("removed") || action.includes("deleted")) {
        return { bg: C.redBg, color: C.red }
    }
    if (action.includes("approved") || action.includes("added") || action.includes("uploaded") || action.includes("created")) {
        return { bg: C.greenBg, color: C.green }
    }
    if (action.includes("updated") || action.includes("reset")) {
        return { bg: C.orangeBg, color: C.orange }
    }
    return { bg: C.bluePale, color: C.blue }
}

function formatActionLabel(action) {
    if (!action) return "—"
    return action.replace(/[._]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

export default function AuditLog() {
    const [records, setRecords] = useState([])
    const [actions, setActions] = useState([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(50)
    const [totalPages, setTotalPages] = useState(1)
    const [loading, setLoading] = useState(true)
    const [searchActor, setSearchActor] = useState("")
    const [filterAction, setFilterAction] = useState("")

    const loadData = useCallback(async () => {
        try {
            setLoading(true)
            const params = new URLSearchParams({ page, limit })
            if (searchActor.trim()) params.set("actorEmail", searchActor.trim())
            if (filterAction) params.set("action", filterAction)
            const res = await api.get(`/admin/audit-logs?${params}`)
            setRecords(res.data.records)
            setTotal(res.data.total)
            setTotalPages(res.data.totalPages)
            setActions(res.data.actions || [])
        } catch (err) {
            console.error("Failed to load audit log:", err)
        } finally {
            setLoading(false)
        }
    }, [page, limit, searchActor, filterAction])

    useEffect(() => { loadData() }, [loadData])

    const handleSearch = () => { setPage(1); loadData() }

    const formatDate = (d) =>
        new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" })

    function getPageNumbers() {
        const pages = []
        const maxVisible = 5
        if (totalPages <= maxVisible + 2) {
            for (let i = 1; i <= totalPages; i++) pages.push(i)
        } else {
            pages.push(1)
            let start = Math.max(2, page - 1)
            let end = Math.min(totalPages - 1, page + 1)
            if (page <= 3) { start = 2; end = Math.min(maxVisible, totalPages - 1) }
            else if (page >= totalPages - 2) { start = Math.max(totalPages - maxVisible + 1, 2); end = totalPages - 1 }
            if (start > 2) pages.push("...")
            for (let i = start; i <= end; i++) pages.push(i)
            if (end < totalPages - 1) pages.push("...")
            pages.push(totalPages)
        }
        return pages
    }

    return (
        <AdminLayout
            title="Audit Log"
            description="Record of every approval, rejection, revocation, and administrative change."
        >
            <div style={{
                width: "100%", maxWidth: "none", margin: 0,
                fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
                paddingBottom: "48px",
            }}>
                <div style={{ marginBottom: "24px" }}>
                    <div style={{
                        background: C.white, border: `1px solid ${C.gray200}`, borderRadius: "14px",
                        padding: "18px 22px", boxShadow: "0 1px 8px rgba(12,35,64,0.05)",
                        display: "inline-flex", alignItems: "center", gap: "14px",
                    }}>
                        <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: C.bluePale, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 800, color: C.blue }}>LOG</div>
                        <div>
                            <div style={{ fontSize: "26px", fontWeight: "800", color: C.blue, lineHeight: 1 }}>{total}</div>
                            <div style={{ fontSize: "11px", fontWeight: "600", color: C.gray400, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "3px" }}>Total Entries</div>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div style={{
                    background: C.white, border: `1px solid ${C.gray200}`,
                    borderRadius: "16px", overflow: "hidden",
                    boxShadow: "0 2px 16px rgba(12,35,64,0.07)",
                }}>
                    <div style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        padding: "18px 24px", borderBottom: `1px solid ${C.gray100}`, background: C.offWhite,
                    }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{
                                width: "32px", height: "32px", borderRadius: "9px",
                                background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
                                display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: 800, color: "#fff",
                            }}>LOG</div>
                            <div>
                                <div style={{ fontSize: "14px", fontWeight: "800", color: C.navy }}>Audit Entries</div>
                                <div style={{ fontSize: "11px", color: C.gray400 }}>Every approval, rejection, revocation, upload, and settings change</div>
                            </div>
                        </div>
                    </div>

                    {/* Search / Filter Bar */}
                    <div style={{
                        padding: "12px 24px", borderBottom: `1px solid ${C.gray100}`, background: C.white,
                        display: "flex", alignItems: "center", gap: "12px",
                    }}>
                        <div style={{ fontSize: "12px", fontWeight: 700, color: C.gray500 }}>Search</div>
                        <input
                            type="text"
                            placeholder="Search by admin email..."
                            value={searchActor}
                            onChange={(e) => setSearchActor(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                            style={{
                                flex: 1, padding: "8px 12px", borderRadius: "8px",
                                border: `1px solid ${C.gray200}`, fontSize: "13px", color: C.navy, outline: "none",
                            }}
                        />
                        <select
                            value={filterAction}
                            onChange={(e) => { setFilterAction(e.target.value); setPage(1) }}
                            style={{
                                padding: "8px 12px", borderRadius: "8px",
                                border: `1px solid ${C.gray200}`, fontSize: "12px", fontWeight: "600",
                                color: C.navy, background: C.offWhite, cursor: "pointer", outline: "none",
                                maxWidth: "220px",
                            }}
                        >
                            <option value="">All Actions</option>
                            {actions.map(a => <option key={a} value={a}>{formatActionLabel(a)}</option>)}
                        </select>
                        <button onClick={handleSearch} style={{
                            padding: "8px 18px", borderRadius: "8px",
                            background: `linear-gradient(135deg, ${C.navy}, ${C.navyMid})`,
                            color: C.white, border: "none", fontSize: "12px", fontWeight: "700", cursor: "pointer",
                        }}>Search</button>
                    </div>

                    {/* Table */}
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: `1.5px solid ${C.gray100}`, background: C.offWhite }}>
                                    {["Action", "Actor", "Target", "Details", "IP Address", "Timestamp"].map(h => (
                                        <th key={h} style={{
                                            padding: "12px 20px", fontSize: "10px", fontWeight: "800",
                                            textTransform: "uppercase", letterSpacing: "0.8px",
                                            color: C.gray400, textAlign: "left", whiteSpace: "nowrap",
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} style={{ padding: "60px 24px", textAlign: "center" }}>
                                            <p style={{ color: C.gray400, fontWeight: 600, fontSize: "14px" }}>Loading...</p>
                                        </td>
                                    </tr>
                                ) : records.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} style={{ padding: "60px 24px", textAlign: "center" }}>
                                            <p style={{ fontSize: "14px", color: C.gray400, fontStyle: "italic" }}>No audit entries found.</p>
                                        </td>
                                    </tr>
                                ) : records.map((r, idx) => {
                                    const style = actionStyle(r.action)
                                    return (
                                        <tr key={r._id} style={{
                                            borderBottom: idx === records.length - 1 ? "none" : `1px solid ${C.gray100}`,
                                        }}>
                                            <td style={{ padding: "14px 20px" }}>
                                                <span style={{
                                                    display: "inline-flex", padding: "4px 10px", borderRadius: "20px",
                                                    fontSize: "10px", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.4px",
                                                    background: style.bg, color: style.color,
                                                }}>
                                                    {formatActionLabel(r.action)}
                                                </span>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ fontSize: "12px", fontWeight: "700", color: C.navy }}>{r.actorEmail || "—"}</div>
                                                {r.actorRole && <div style={{ fontSize: "10px", color: C.gray400 }}>{r.actorRole}</div>}
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ fontSize: "12px", color: C.gray700 }}>{r.targetLabel || r.targetType || "—"}</div>
                                                {r.targetType && <div style={{ fontSize: "10px", color: C.gray400 }}>{r.targetType}</div>}
                                            </td>
                                            <td style={{ padding: "14px 20px", maxWidth: "260px" }}>
                                                {r.metadata && Object.keys(r.metadata).length > 0 ? (
                                                    <div style={{
                                                        fontSize: "11px", color: C.gray500, fontFamily: "monospace",
                                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                                    }} title={JSON.stringify(r.metadata)}>
                                                        {JSON.stringify(r.metadata)}
                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: "12px", color: C.gray300, fontStyle: "italic" }}>—</span>
                                                )}
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <span style={{ fontSize: "12px", color: C.gray700, fontFamily: "monospace" }}>{r.ip || "—"}</span>
                                            </td>
                                            <td style={{ padding: "14px 20px" }}>
                                                <div style={{ fontSize: "12px", color: C.gray700, fontWeight: "600" }}>{formatDate(r.created_on)}</div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Footer */}
                    {records.length > 0 && (
                        <div style={{
                            padding: "14px 24px", borderTop: `1px solid ${C.gray100}`, background: C.offWhite,
                            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                    <span style={{ fontSize: "12px", color: C.gray500, fontWeight: "600" }}>Rows per page:</span>
                                    <select
                                        value={limit}
                                        onChange={(e) => { setLimit(Number(e.target.value)); setPage(1) }}
                                        style={{
                                            padding: "4px 8px", borderRadius: "6px",
                                            border: `1px solid ${C.gray200}`, fontSize: "12px", fontWeight: "700",
                                            color: C.navy, background: C.white, cursor: "pointer", outline: "none",
                                        }}
                                    >
                                        {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <span style={{ fontSize: "12px", color: C.gray400 }}>
                                    {Math.min((page - 1) * limit + 1, total)}–{Math.min(page * limit, total)} of {total}
                                </span>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <button onClick={() => setPage(1)} disabled={page <= 1}
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page <= 1 ? C.gray300 : C.navy, cursor: page <= 1 ? "not-allowed" : "pointer" }}>«</button>
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page <= 1 ? C.gray300 : C.navy, cursor: page <= 1 ? "not-allowed" : "pointer" }}>‹</button>
                                {getPageNumbers().map((p, i) =>
                                    p === "..." ? (
                                        <span key={`ellipsis-${i}`} style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", color: C.gray400, fontWeight: "700" }}>…</span>
                                    ) : (
                                        <button key={p} onClick={() => setPage(p)}
                                            style={{ width: "32px", height: "32px", borderRadius: "8px", border: p === page ? "none" : `1px solid ${C.gray200}`, background: p === page ? `linear-gradient(135deg, ${C.navy}, ${C.navyMid})` : C.white, fontSize: "12px", fontWeight: "700", color: p === page ? C.white : C.navy, cursor: "pointer" }}>{p}</button>
                                    )
                                )}
                                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page >= totalPages ? C.gray300 : C.navy, cursor: page >= totalPages ? "not-allowed" : "pointer" }}>›</button>
                                <button onClick={() => setPage(totalPages)} disabled={page >= totalPages}
                                    style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.gray200}`, background: C.white, fontSize: "13px", color: page >= totalPages ? C.gray300 : C.navy, cursor: page >= totalPages ? "not-allowed" : "pointer" }}>»</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AdminLayout>
    )
}
